/**
 * EuHistoricalCompareLauncher — US-style entry point into the EU historical
 * SmPC compare, embedded in the EU drug view: a "Compare: [Select version…]
 * [Compare]" control (mirrors the US version-compare dropdown). Picking a
 * historical version and clicking Compare opens the two-pane PDF compare of
 * (selected version → current). Self-gates: renders nothing unless the drug has
 * >=2 archived historical versions in SOR.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { auth } from '../../config/firebase';
import EuHistoricalCompare from './EuHistoricalCompare';
import {
  EU_HISTORY_COMPARE_SOURCE,
  clearEuHistoricalCompareTarget,
} from '../../utils/euHistoricalCompareLink';

const BASE_URL =
  (import.meta.env['VITE_API_URL'] as string) ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

interface Ver { vstr: string; date: string; docid: string }

// EU drug set_id (emea/h/c/002455) → archive H/C key (EMEA/H/C/002455).
function hcFromSetId(setId: string): string {
  return setId.trim().toUpperCase();
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDate(d: string): string {
  const dt = new Date(`${d}T00:00:00`);
  if (isNaN(dt.getTime())) return d;
  return `${MON[dt.getMonth()]} ${String(dt.getDate()).padStart(2, '0')} ${dt.getFullYear()}`;
}

// A chat-citation deep-link target: the version pair plus the full landing
// target (section / presentation / block) the compare uses to scroll + outline
// the exact change. ``section``/``presentation``/``block`` are optional — an
// inline section link with no interval opens the pair only.
interface DeepLinkTarget {
  old: string; new: string;
  section?: string | undefined;
  presentation?: string | undefined;
  block?: string | undefined;
  part?: string | undefined;   // flag-ON Parser-B path — namespaces the block
}

export default function EuHistoricalCompareLauncher({ drugId, setId, brand }: {
  drugId?: number | undefined; setId: string; brand: string;
}) {
  const [versions, setVersions] = useState<Ver[]>([]);
  const [sel, setSel] = useState('');       // selected historical vstr (the "old" side)
  const [open, setOpen] = useState(false);
  // Deep-link target from a chat citation click (?source=eu-history-compare&…).
  // When set, the modal opens on this exact pair + landing target instead of the
  // dropdown pick.
  const [deepLink, setDeepLink] = useState<DeepLinkTarget | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const hc = hcFromSetId(setId || '');

  useEffect(() => {
    if (!hc) return;
    let alive = true;
    (async () => {
      try {
        const u = auth.currentUser;
        const headers: Record<string, string> = {};
        if (u) headers['Authorization'] = `Bearer ${await u.getIdToken(false)}`;
        const r = await fetch(
          `${BASE_URL}/eu-compare-proto/versions?hc=${encodeURIComponent(hc)}`,
          { headers });
        if (!r.ok) return;
        const d = await r.json();
        if (alive) setVersions(d.versions || []);
      } catch { /* archive optional — stay hidden on error */ }
    })();
    return () => { alive = false; };
  }, [hc]);

  // Chat-citation deep-link: open the compare at the cited version pair + land
  // on the exact target. Runs once versions have loaded (need the newest
  // archived as the default "new" side when the citation only carries a "from").
  //
  // The params are KEPT (not cleared here) until the modal closes, so a refresh
  // mid-modal re-lands on the same target. Without an explicit ``from`` the
  // target is an ambiguous section-only link — we do NOT auto-open a forced pair
  // (the launcher stays on its dropdown so the user picks the interval).
  useEffect(() => {
    if (searchParams.get('source') !== EU_HISTORY_COMPARE_SOURCE) return;
    if (versions.length < 2) return;
    const from = searchParams.get('from') || '';
    const to = searchParams.get('to') || '';
    const newest = versions[versions.length - 1]!.vstr;
    if (!from) return;   // no forced pair → leave closed, don't guess an interval
    setDeepLink({
      old: from, new: to || newest,
      section: searchParams.get('section') || undefined,
      presentation: searchParams.get('presentation') || undefined,
      block: searchParams.get('block') || undefined,
      part: searchParams.get('part') || undefined,
    });
    setOpen(true);
  }, [searchParams, versions]);

  // Close = drop the deep-link + strip the canonical params so back/refresh
  // leaves a clean URL (and doesn't silently re-open).
  const closeModal = () => {
    setOpen(false);
    setDeepLink(null);
    const { next, changed } = clearEuHistoricalCompareTarget(searchParams);
    if (changed) setSearchParams(next, { replace: true });
  };

  if (versions.length < 2) return null;  // not archived → no launcher

  const latest = versions[versions.length - 1]!.vstr;   // "current" = newest archived
  // Newest-first, labelled "SmPC - V{ordinal} - Mon DD YYYY" (ordinal = chrono
  // position, oldest=1). Exclude the current version — comparing it to itself.
  const options = versions
    .map((v, i) => ({ vstr: v.vstr, ord: i + 1, date: v.date }))
    .filter((o) => o.vstr !== latest)
    .reverse();

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[13px] text-ink-2">Compare:</span>
      <select
        value={sel}
        onChange={(e) => setSel(e.target.value)}
        className="h-9 min-w-[220px] rounded-ctl border border-line-input bg-white px-2 text-sm text-ink focus:outline-none focus:ring-4 focus:ring-accent-ring">
        <option value="">Select version…</option>
        {options.map((o) => (
          <option key={o.vstr} value={o.vstr}>SmPC - V{o.ord} - {fmtDate(o.date)}</option>
        ))}
      </select>
      <button
        disabled={!sel}
        onClick={() => setOpen(true)}
        className="btn-secondary disabled:opacity-50">
        Compare
      </button>
      {/* BETA until the EU version source (EMA revision vs EC decision) is settled */}
      <span title="Beta: EU versions are still being validated against the EMA / EC sources" className="rounded-ctl border border-signal/30 px-1.5 font-mono text-[10px] tracking-[0.08em] text-signal">BETA</span>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3"
             onClick={closeModal}>
          <div className="bg-white rounded-lg shadow-2xl w-full h-full max-w-[1500px] max-h-[93vh] overflow-hidden"
               onClick={(e) => e.stopPropagation()}>
            <EuHistoricalCompare
              hc={hc} brand={brand} drugId={drugId}
              initialOld={deepLink ? deepLink.old : sel}
              initialNew={deepLink ? deepLink.new : latest}
              initialSection={deepLink?.section}
              initialPresentation={deepLink?.presentation}
              initialBlockId={deepLink?.block}
              initialDocumentPart={deepLink?.part}
              onClose={closeModal} />
          </div>
        </div>
      )}
    </div>
  );
}
